import {ArtifactId, ExpeditionId, ProjectId} from "../../shared/types/ids";
import {DateTime, UserMessage} from '@webmate-web-core/shared/types/base-types';

export type ArtifactType = string;

export interface ArtifactAssociation {
    id: string,
    associationType: string,
    relationName: string
}

export namespace WellKnownArtifactTypes {
    export const ActionStart = 'Action.ActionStart';
    export const ActionFinish = 'Action.ActionFinish';
    export const BrowserSessionState = 'Action.BrowserSessionState';
    export const Screenshot = 'Page.Screenshot';
    export const Image = 'Image.Image';
    export const A11yReport = 'Page.A11yReport';
    export const FullpageScreenshot = 'Page.FullpageScreenshot';
    export const MediaInfo = 'Page.MediaInfo';
    export const Video = 'Session.Video';
    export const VideoRecording = 'Video.Recording';
    export const Har = 'Network.Har';
    export const PlaywrightTrace = 'Trace.Playwright';
    export const FileContent = 'File.Content';
    export const DataLayer = 'Tracking.DataLayer';
    export const WebtrekkSendInfo = 'Tracking.WebtrekkSendInfo';
    export const DomLayoutTree = 'DOM.DomLayoutTree';
    export const ContentAnalysis = 'Page.ContentAnalysis';
    export const MobileAccessibilityAnalysis = 'Mobile.AccessibilityAnalysis';
    export const ImageTextContrastAnalysis = 'Image.ContrastAnalysis';
    export const TabOrderAnalysis = 'DOM.TabOrderAnalysis';
    export const TestMailAccount = 'TestMailAccount';
}

export namespace WellKnownArtifactEvents {
    export const ArtifactAdded = 'Artifact.ArtifactsAdded';
    export const ArtifactUpdated = 'Artifact.ArtifactUpdated';
}

export const artifactTypeToIcon = {
    'Action.ActionStart': 'play',
    'DOM.DomLayoutTree': 'sitemap',
    'Page.FullpageScreenshot': 'image',
    'Page.Screenshot': 'image',
    'Image.Image': 'image',
    'Session.Video': 'video',
    'Video.Recording': 'video',
    'Network.Har': 'network-wired',
    'Trace.Playwright': 'route',
    'File.Content': 'file',
    'TestRun.Output': 'terminal',
};

export interface ArtifactProductionInfo {
    warnings: UserMessage[];
}

/**
 * Represents a webmate Artifact.
 */
export interface Artifact {
    id: ArtifactId;
    artifactType: ArtifactType;
    projectId: ProjectId;
    associations: ArtifactAssociation[];
    creationTime: DateTime;
    endTime: DateTime;
    data: any;
    version?: number;
    productionInfo?: ArtifactProductionInfo;
}

export function isVideo(artifactType: ArtifactType): boolean {
    return artifactType === 'Session.Video' || artifactType === 'Video.Recording';
}

export function isScreenshot(artifactType: ArtifactType): boolean {
    return artifactType === 'Page.Screenshot' || artifactType === 'Page.FullpageScreenshot' || artifactType === 'Image.Image';
}

export function isAction(artifactType: ArtifactType): boolean {
    return artifactType === 'Action.ActionStart' || artifactType === 'Action.ActionFinish' || artifactType === 'Action.BrowserSessionState';
}

export function isActionNoState(artifactType: ArtifactType): boolean {
    return artifactType === 'Action.ActionStart' || artifactType === 'Action.ActionFinish';
}

export function isState(artifactType: ArtifactType): boolean {
    return artifactType === 'Action.BrowserSessionState';
}

/**
 * Contains all Artifact information without the data.
 */
export interface ArtifactInfo {
    id: ArtifactId;
    artifactType: ArtifactType;
    projectId: ProjectId;
    associations: ArtifactAssociation[];
    creationTime: DateTime;
    endTime: DateTime;
    version?: number;
    productionInfo?: ArtifactProductionInfo;
}

/////// Artifacts //////
export interface MediaInfoData {
    pdfs: any[];
    images: any[];
    videos: any[];
    audios: any[];
    svgs: any[];
    canvases: any[];
}
